import { useState, useEffect, useCallback } from "react";
import servicesService from "../../../api/servicesService";
import clientsService from "../../../api/clientsService";
import techniciansService from "../../../api/techniciansService";
import branchesService from "../../../api/branchesService";
import { normalizeServicePayload, dateToISO } from "../utils";

const parseMonthFilter = (monthFilter) => {
  if (!monthFilter) return {};
  const [year, month] = monthFilter.split("-").map(Number);
  return { year, month };
};

/**
 * Hook personalizado para manejar la lógica de servicios
 */
export const useServices = (monthFilter) => {
  const [servicios, setServicios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSelects, setLoadingSelects] = useState(false);
  const [clientesLoaded, setClientesLoaded] = useState(false);

  const loadTecnicos = useCallback(async () => {
    const techRes = await techniciansService.getAll();
    setTecnicos(techRes.data.data || []);
  }, []);

  const loadClientes = useCallback(async () => {
    if (clientesLoaded) return clientes;
    const cliRes = await clientsService.getAll();
    const data = cliRes.data.data || [];
    setClientes(data);
    setClientesLoaded(true);
    return data;
  }, [clientes, clientesLoaded]);

  const loadServicios = useCallback(async (targetMonth = monthFilter) => {
    try {
      setLoading(true);
      setServicios([]);
      const params = parseMonthFilter(targetMonth);
      const res = await servicesService.getAll(params);
      const data = res.data.data || [];
      setServicios(data);
      return data;
    } catch (err) {
      console.error("Error cargando servicios:", err);
      setServicios([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [monthFilter]);

  useEffect(() => {
    loadTecnicos().catch((err) => {
      console.error("Error cargando técnicos:", err);
    });
  }, [loadTecnicos]);

  useEffect(() => {
    loadServicios(monthFilter);
  }, [monthFilter, loadServicios]);

  const createService = useCallback(async (formData) => {
    try {
      const payload = normalizeServicePayload({
        ...formData,
        date: formData.date ? dateToISO(formData.date) : "",
      });
      await servicesService.create(payload);
      await loadServicios(monthFilter);
      return true;
    } catch (err) {
      console.error("Error creando servicio:", err);
      throw err;
    }
  }, [loadServicios, monthFilter]);

  const updateService = useCallback(
    async (id, formData) => {
      try {
        const payload = normalizeServicePayload({
          ...formData,
          date: formData.date ? dateToISO(formData.date) : undefined,
        });

        await servicesService.update(id, payload);
        await loadServicios(monthFilter);
        return true;
      } catch (err) {
        console.error("Error actualizando servicio:", err);
        throw err;
      }
    },
    [loadServicios, monthFilter]
  );

  const deleteService = useCallback(async (id) => {
    try {
      await servicesService.delete(id);
      await loadServicios(monthFilter);
      return true;
    } catch (err) {
      console.error("Error eliminando servicio:", err);
      throw err;
    }
  }, [loadServicios, monthFilter]);

  return {
    servicios,
    clientes,
    tecnicos,
    loading,
    loadingSelects,
    setLoadingSelects,
    loadServicios,
    loadClientes,
    createService,
    updateService,
    deleteService,
  };
};

/**
 * Hook para manejar sucursales por cliente
 */
export const useBranches = () => {
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadBranchesByClient = useCallback(async (clientId) => {
    try {
      setLoading(true);
      if (!clientId) {
        setSucursales([]);
        return [];
      }
      const res = await branchesService.getByClient(clientId);
      const data = res.data.data || [];
      setSucursales(data);
      return data;
    } catch (err) {
      console.error("Error cargando sucursales:", err);
      setSucursales([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    sucursales,
    loading,
    loadBranchesByClient,
    setSucursales,
  };
};
